import { Launch } from '@/redux/slices/focusNewScheduleSlice';
import {
  IncrementalLaunchRightsSelectionState,
  IncrementalLaunchSelectedVersions
} from '@/components/Focus/AvailsSchedule/NewSchedule/ReviewSchedule/AddIncrementalLaunch/IncrementalLaunchWorkspace';
import { IncrementalLaunchTimingValue } from '@/components/Focus/AvailsSchedule/NewSchedule/ReviewSchedule/AddIncrementalLaunch/IncrementalLaunchTimingSection/IncrementalLaunchTimingSection';
import { TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { applyLaunchRightsDraft, applyLaunchVersionsDraft } from '@/components/Focus/utils/newScheduleAvails';

type PreviousIncrementChanges = {
  selectedDubs: string[];
  selectedSubs: string[];
  selectedTerritories: string[];
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
};

type ResolvedRightsState = {
  territories: string[];
  audio: string[];
  timedtext: string[];
  versions: (TitleEntity | TitleEntityV2)['id'][];
};

export type ResolvedIncrementalLaunchDisplayState = {
  selectedTerritories: string[];
  selectedDubs: string[];
  selectedSubs: string[];
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
  occurrenceStartDateIso: string;
  offsetValue: number;
  offsetUnit: string;
};

// Resolves one launch timing on top of the already resolved previous release time.
// This is the timing equivalent of applying one launch rights draft.
const getResolvedReleaseTime = ({
  previousLaunchReleaseTime,
  timing
}: {
  previousLaunchReleaseTime: Date;
  timing: IncrementalLaunchTimingValue;
}): Date => {
  const { offsetUnit, offsetValue, releaseTime } = timing;

  const result = new Date(previousLaunchReleaseTime);

  if (offsetUnit === 'Hours') {
    result.setTime(result.getTime() + offsetValue * 60 * 60 * 1000);
  } else if (offsetUnit === 'Days') {
    result.setDate(result.getDate() + offsetValue);
  } else if (offsetUnit === 'Weeks') {
    result.setDate(result.getDate() + offsetValue * 7);
  }

  if (offsetUnit !== 'Hours' && releaseTime) {
    result.setHours(releaseTime.getHours(), releaseTime.getMinutes(), 0, 0);
  }

  return result;
};

// Creates the add/remove delta needed to turn previousValues into currentValues.
// Used when rebuilding downstream launch drafts after an edit or delete.
const createRebasedSelectionState = (previousValues: string[], currentValues: string[]): IncrementalLaunchRightsSelectionState => ({
  add: currentValues.filter((value) => !previousValues.includes(value)),
  remove: previousValues.filter((value) => !currentValues.includes(value))
});

// Creates the add/remove delta needed to turn previousValues into currentValues.
// Used when rebuilding downstream launch drafts after an edit or delete.
const createRebasedSelectedVersionsState = (
  previousValues: (TitleEntity | TitleEntityV2)['id'][],
  currentValues: (TitleEntity | TitleEntityV2)['id'][]
): IncrementalLaunchSelectedVersions => ({
  add: currentValues.filter((value) => !previousValues.includes(value)),
  remove: previousValues.filter((value) => !currentValues.includes(value))
});

// Resolves rights through a specific launch index, inclusive.
// launchList[0] is treated as the base launch, so incremental launches start at index 1.
const getResolvedRightsStateAtIndex = ({
  launchList,
  index,
  selectedTerritories,
  selectedDubs,
  selectedSubs,
  selectedVersions
}: {
  launchList: Launch[];
  index: number;
  selectedTerritories: string[];
  selectedDubs: string[];
  selectedSubs: string[];
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
}): ResolvedRightsState => {
  const resolvedRights: ResolvedRightsState = {
    territories: selectedTerritories,
    audio: selectedDubs,
    timedtext: selectedSubs,
    versions: selectedVersions
  };

  for (let i = 1; i <= index; i++) {
    const draft = launchList[i]?.incrementalLaunchRightsDraftByTab;
    const versions = launchList[i]?.incrementalLaunchVersions;

    if (!draft) {
      continue;
    }

    resolvedRights.territories = applyLaunchRightsDraft({
      previousValues: resolvedRights.territories,
      addedValues: draft.territories?.add,
      removedValues: draft.territories?.remove
    });

    resolvedRights.audio = applyLaunchRightsDraft({
      previousValues: resolvedRights.audio,
      addedValues: draft.audio?.add,
      removedValues: draft.audio?.remove
    });

    resolvedRights.timedtext = applyLaunchRightsDraft({
      previousValues: resolvedRights.timedtext,
      addedValues: draft.timedtext?.add,
      removedValues: draft.timedtext?.remove
    });

    resolvedRights.versions = applyLaunchVersionsDraft({
      previousValues: resolvedRights.versions,
      addedValues: versions?.add,
      removedValues: versions?.remove
    });
  }

  return resolvedRights;
};

// Recomputes downstream launch deltas after a launch is edited or removed.
//
// For each launch at or after startIndex:
// 1. Resolve the rights state before that launch
// 2. Resolve the rights state at that launch
// 3. Rebuild that launch's add/remove delta from those two resolved states
export const rebaseFollowingLaunches = ({
  launchList,
  startIndex,
  selectedTerritories,
  selectedDubs,
  selectedSubs,
  selectedVersions
}: {
  launchList: Launch[];
  startIndex: number;
  selectedTerritories: string[];
  selectedDubs: string[];
  selectedSubs: string[];
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
}): Launch[] => {
  const nextLaunchList = [...launchList];

  const baseResolvedRights: ResolvedRightsState = {
    territories: selectedTerritories,
    audio: selectedDubs,
    timedtext: selectedSubs,
    versions: selectedVersions
  };

  for (let index = startIndex; index < nextLaunchList.length; index++) {
    if (index === 0) {
      continue;
    }

    const previousResolved =
      index === 1
        ? baseResolvedRights
        : getResolvedRightsStateAtIndex({
            launchList: nextLaunchList,
            index: index - 1,
            selectedTerritories,
            selectedDubs,
            selectedSubs,
            selectedVersions
          });

    const currentResolved = getResolvedRightsStateAtIndex({
      launchList: nextLaunchList,
      index,
      selectedTerritories,
      selectedDubs,
      selectedSubs,
      selectedVersions
    });

    nextLaunchList[index] = {
      ...nextLaunchList[index],
      incrementalLaunchRightsDraftByTab: {
        ...nextLaunchList[index].incrementalLaunchRightsDraftByTab,
        territories: createRebasedSelectionState(previousResolved.territories, currentResolved.territories),
        audio: createRebasedSelectionState(previousResolved.audio, currentResolved.audio),
        timedtext: createRebasedSelectionState(previousResolved.timedtext, currentResolved.timedtext)
      },
      incrementalLaunchVersions: createRebasedSelectedVersionsState(previousResolved.versions, currentResolved.versions)
    };
  }

  return nextLaunchList;
};

// Resolves the state that exists before a given incremental launch is applied.
//
// Behavior:
// - if launchId is provided, returns the rights state before that specific launch
// - if launchId is omitted, returns the rights state before adding a brand new launch at the end
// - launchList[0] is treated as the base launch, so incremental launches are resolved from index 1 onward
export const getPreviousIncrementChanges = ({
  launchList,
  originalSelectedDubs,
  originalSelectedSubs,
  originalSelectedTerritories,
  originalSelectedVersions,
  launchId
}: {
  launchList: Launch[];
  originalSelectedDubs: string[];
  originalSelectedSubs: string[];
  originalSelectedTerritories: string[];
  originalSelectedVersions: (TitleEntity | TitleEntityV2)['id'][];
  launchId?: string;
}): PreviousIncrementChanges => {
  const returnValue: PreviousIncrementChanges = {
    selectedDubs: originalSelectedDubs,
    selectedSubs: originalSelectedSubs,
    selectedTerritories: originalSelectedTerritories,
    selectedVersions: originalSelectedVersions
  };

  if (!launchList.length) {
    return returnValue;
  }

  const incrementIndex = launchId ? launchList.findIndex((launch) => launch.id === launchId) : launchList.length;

  if (incrementIndex === -1 || incrementIndex < 2) {
    return returnValue;
  }

  for (let i = 1; i < incrementIndex; i++) {
    const draft = launchList[i]?.incrementalLaunchRightsDraftByTab;
    const versions = launchList[i]?.incrementalLaunchVersions;

    if (!draft) {
      continue;
    }

    returnValue.selectedDubs = applyLaunchRightsDraft({
      previousValues: returnValue.selectedDubs,
      addedValues: draft.audio?.add,
      removedValues: draft.audio?.remove
    });

    returnValue.selectedSubs = applyLaunchRightsDraft({
      previousValues: returnValue.selectedSubs,
      addedValues: draft.timedtext?.add,
      removedValues: draft.timedtext?.remove
    });

    returnValue.selectedTerritories = applyLaunchRightsDraft({
      previousValues: returnValue.selectedTerritories,
      addedValues: draft.territories?.add,
      removedValues: draft.territories?.remove
    });

    if (!versions) continue;

    returnValue.selectedVersions = applyLaunchVersionsDraft({
      previousValues: returnValue.selectedVersions,
      addedValues: versions?.add,
      removedValues: versions?.remove
    });
  }

  return returnValue;
};

// Resolves the release time that exists before a given incremental launch is applied.
//
// Behavior:
// - if launchId is provided, returns the release time before that specific launch
// - if launchId is omitted, returns the release time before adding a brand new launch at the end
// - launchList[0] is treated as the base launch, so incremental launches are resolved from index 1 onward
export const getPreviousLaunchReleaseTime = ({
  launchList,
  launchId,
  initialReleaseDateIso
}: {
  launchList: Launch[];
  launchId?: string;
  initialReleaseDateIso: string;
}): Date => {
  const initialReleaseTime = new Date(initialReleaseDateIso);

  if (!launchList.length) {
    return initialReleaseTime;
  }

  const incrementIndex = launchId ? launchList.findIndex((launch) => launch.id === launchId) : launchList.length;

  if (incrementIndex === -1 || incrementIndex < 2) {
    return initialReleaseTime;
  }

  let previousLaunchReleaseTime = initialReleaseTime;

  for (let i = 1; i < incrementIndex; i++) {
    const timing = launchList[i]?.incrementalLaunchTimingValue;

    if (!timing) {
      continue;
    }

    previousLaunchReleaseTime = getResolvedReleaseTime({
      previousLaunchReleaseTime,
      timing
    });
  }

  return previousLaunchReleaseTime;
};

export const getResolvedVersionLaunchDisplayState = ({
  launch,
  launchList,
  initialReleaseDateIso,
  originalSelectedTerritories,
  originalSelectedDubs,
  originalSelectedSubs,
  originalSelectedVersions
}: {
  launch: Launch;
  launchList: Launch[];
  initialReleaseDateIso: string;
  originalSelectedTerritories: string[];
  originalSelectedDubs: string[];
  originalSelectedSubs: string[];
  originalSelectedVersions: (TitleEntity | TitleEntityV2)['id'][];
}): ResolvedIncrementalLaunchDisplayState => {
  const previousChanges = getPreviousIncrementChanges({
    launchList,
    originalSelectedDubs,
    originalSelectedSubs,
    originalSelectedTerritories,
    originalSelectedVersions,
    launchId: launch.id
  });

  const draft = launch.incrementalLaunchRightsDraftByTab;
  const versions = launch.incrementalLaunchVersions;
  const timing = launch.incrementalLaunchTimingValue;
  const previousLaunchReleaseTime = getPreviousLaunchReleaseTime({
    launchList,
    launchId: launch.id,
    initialReleaseDateIso
  });

  const selectedTerritories = draft
    ? applyLaunchRightsDraft({
        previousValues: previousChanges.selectedTerritories,
        addedValues: draft.territories?.add,
        removedValues: draft.territories?.remove
      })
    : previousChanges.selectedTerritories;

  const selectedDubs = draft
    ? applyLaunchRightsDraft({
        previousValues: previousChanges.selectedDubs,
        addedValues: draft.audio?.add,
        removedValues: draft.audio?.remove
      })
    : previousChanges.selectedDubs;

  const selectedSubs = draft
    ? applyLaunchRightsDraft({
        previousValues: previousChanges.selectedSubs,
        addedValues: draft.timedtext?.add,
        removedValues: draft.timedtext?.remove
      })
    : previousChanges.selectedSubs;

  const selectedVersions = versions
    ? applyLaunchVersionsDraft({
        previousValues: previousChanges.selectedVersions,
        addedValues: versions?.add,
        removedValues: versions?.remove
      })
    : previousChanges.selectedVersions;

  const resolvedReleaseTime = timing
    ? getResolvedReleaseTime({
        previousLaunchReleaseTime,
        timing
      })
    : previousLaunchReleaseTime;

  return {
    selectedTerritories,
    selectedDubs,
    selectedSubs,
    selectedVersions,
    occurrenceStartDateIso: resolvedReleaseTime.toISOString(),
    offsetValue: timing?.offsetValue ?? 0,
    offsetUnit: timing?.offsetUnit ?? ''
  };
};
